import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Calendar } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import { timezones } from '../../../../utils/timezones';
import { handleErrors } from '../../../../utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, updateCalendar } from '../../../../api/users-api';
import { useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { calendarColors } from '../../../../utils/calendar-colors';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

interface CalendarEditDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedCalendar: Calendar | null;
  calendarRoles: string[];
  changeLogRoles: string[];
  isAllUsers: boolean;
}

export default function CalendarEditDialog({
  open,
  handleClose,
  userEmail,
  selectedCalendar,
  calendarRoles,
  changeLogRoles,
  isAllUsers,
}: CalendarEditDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isCreating } = useMutation({
    mutationFn: (data: Partial<Calendar>) =>
      updateCalendar(userEmail, data, changeLogMessage),
    onSuccess: (_returnData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar edited successfully!',
      });

      queryClient.setQueriesData(
        { queryKey: [UserKeys.Calendars, userEmail] },
        (oldData: Calendar[] | undefined) => {
          if (oldData) {
            return oldData.map((calendar) => {
              if (calendar.id !== data!.id) {
                return calendar;
              }

              return {
                ...data,
                backgroundColor:
                  calendarColors.calendar[
                    data.colorId! as keyof typeof calendarColors.calendar
                  ].background,
              };
            });
          }
          return oldData;
        }
      );

      setChangeLogMessage('');
      reset();
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue editing a calendar. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        isAllUsers
          ? analytics.calendars.calendars.update_calendar
          : analytics.user_management.user_details.calendars.update_calendar,
        isAllUsers
          ? {
              module: ModuleList.modules.calendars.name,
              section: ModuleList.modules.calendars.sections.calendars,
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.calendars,
            }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isLoading, errors, dirtyFields },
  } = useForm({
    values: {
      ...selectedCalendar,
      summary: selectedCalendar?.summary || '',
      description: selectedCalendar?.description || '',
      location: selectedCalendar?.location || '',
      timeZone: selectedCalendar?.timeZone || '',
      colorId: selectedCalendar?.colorId || '',
      hidden: selectedCalendar?.hidden || false,
      selected: selectedCalendar?.selected || false,
    },
  });

  const onSubmit: SubmitHandler<Partial<Calendar>> = (data) => {
    if (
      (dirtyFields.summary &&
        isAbleToSeeUIElement(
          changeLogRoles,
          CalendarRoles.RenameCalendars.value
        )) ||
      (dirtyFields.description &&
        isAbleToSeeUIElement(
          changeLogRoles,
          CalendarRoles.EditCalendarDescription.value
        )) ||
      (dirtyFields.location &&
        isAbleToSeeUIElement(
          changeLogRoles,
          CalendarRoles.EditCalendarLocation.value
        )) ||
      (dirtyFields.timeZone &&
        isAbleToSeeUIElement(
          changeLogRoles,
          CalendarRoles.EditCalendarTimezone.value
        )) ||
      (dirtyFields.colorId &&
        isAbleToSeeUIElement(
          changeLogRoles,
          CalendarRoles.EditCalendarColor.value
        )) ||
      (dirtyFields.selected &&
        isAbleToSeeUIElement(
          changeLogRoles,
          CalendarRoles.EditCalendarVisibility.value
        ))
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate({
        id: selectedCalendar?.id,
        ...data,
      });
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        reset();
      }}
      title="Edit Calendar"
      dialogContent={
        <Box>
          <Grid
            container
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid size={{ xs: 6 }} gap={4}>
              <Controller
                name="summary"
                control={control}
                rules={{
                  required: 'Calendar Name is required.',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoFocus
                    label="Calendar Name"
                    autoComplete="off"
                    error={!!errors.summary}
                    helperText={errors?.summary?.message || ' '}
                    sx={{ minWidth: 250 }}
                    disabled={
                      !isAbleToSeeUIElement(
                        calendarRoles,
                        CalendarRoles.RenameCalendars.value
                      )
                    }
                    required
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    autoComplete="off"
                    helperText={errors?.description?.message || ' '}
                    sx={{ minWidth: 250 }}
                    disabled={
                      !isAbleToSeeUIElement(
                        calendarRoles,
                        CalendarRoles.EditCalendarDescription.value
                      )
                    }
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Location"
                    autoComplete="off"
                    helperText={errors?.location?.message || ' '}
                    sx={{ minWidth: 250 }}
                    disabled={
                      !isAbleToSeeUIElement(
                        calendarRoles,
                        CalendarRoles.EditCalendarLocation.value
                      )
                    }
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="timeZone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'timeZone',
                    }}
                    InputLabelProps={{
                      htmlFor: 'timeZone',
                    }}
                    select
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                          },
                        },
                      },
                    }}
                    label="Time Zone"
                    sx={{ minWidth: 250 }}
                    disabled={
                      !isAbleToSeeUIElement(
                        calendarRoles,
                        CalendarRoles.EditCalendarTimezone.value
                      )
                    }
                  >
                    {timezones.map((timezone) => (
                      <MenuItem key={timezone} value={timezone}>
                        {timezone}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="colorId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'colorId',
                    }}
                    InputLabelProps={{
                      htmlFor: 'colorId',
                    }}
                    select
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                          },
                        },
                      },
                    }}
                    label="Color"
                    sx={{ width: 80 }}
                    disabled={
                      !isAbleToSeeUIElement(
                        calendarRoles,
                        CalendarRoles.EditCalendarColor.value
                      )
                    }
                  >
                    {Object.keys(calendarColors.calendar).map((colorId) => (
                      <MenuItem key={colorId} value={colorId}>
                        <Box
                          sx={{
                            backgroundColor:
                              calendarColors.calendar[
                                colorId as keyof typeof calendarColors.calendar
                              ].background,
                            width: 20,
                            height: 20,
                            borderRadius: 1,
                            mx: 'auto',
                          }}
                        ></Box>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="hidden"
                control={control}
                render={({ field: props }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        checked={!props.value}
                        onChange={(e) => props.onChange(!e.target.checked)}
                      />
                    }
                    label="Is Shown"
                  />
                )}
              />
              <Controller
                name="selected"
                control={control}
                render={({ field: props }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => props.onChange(e.target.checked)}
                        disabled={
                          !isAbleToSeeUIElement(
                            calendarRoles,
                            CalendarRoles.EditCalendarVisibility.value
                          )
                        }
                      />
                    }
                    label="Is Selected"
                  />
                )}
              />
            </Grid>
          </Grid>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() =>
              mutate({
                id: selectedCalendar?.id,
                ...getValues(),
              })
            }
            mutateLoading={isCreating}
          />
        </Box>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit(onSubmit),
        loading: isCreating || isLoading,
        disabled: isCreating || isLoading,
      }}
    />
  );
}
